"use client";

import { useOrganization, useUser } from "@clerk/nextjs";
import { usePusherOrgChannel, usePusherUserChannel } from "@cr/react-client/hooks/pusher-hooks";
import queryClient from "@cr/react-client/query";
import { WEBSOCKET_MESSAGE_TYPES } from "@/types/consts/websockets";
import { type ServerToastMessageDto } from "@cr/shared-types/dtos/websockets.dto";
import { useCallback, useEffect } from "react";
import { customToast } from "@cr/react-client/toast";

const PushNotification = () => {
    const { organization, isLoaded: isOrganizationLoaded } = useOrganization();
    const { user, isLoaded: isUserLoaded } = useUser();
    const orgChannel = usePusherOrgChannel(isOrganizationLoaded ? organization?.id : null);
    const userChannel = usePusherUserChannel(isUserLoaded ? user?.id : null);

    const handleToast = useCallback((message: ServerToastMessageDto) => {
        if (message.reactQueryKey) {
            // Handle multiple formats for backwards compatibility:
            // 1. Single string: "files"
            // 2. Single array: ["files", fileId]
            // 3. Array of arrays: [["files", fileId], ["validation.files", fileId]]
            let queryKeys: Array<string | string[]>;

            if (typeof message.reactQueryKey === "string") {
                // Single string - wrap in array
                queryKeys = [message.reactQueryKey];
            } else if (Array.isArray(message.reactQueryKey)) {
                // Check if it's an array of arrays or a single query key array
                const firstElement = message.reactQueryKey[0];
                if (Array.isArray(firstElement)) {
                    // Array of arrays: [["files", fileId], ["validation.files", fileId]]
                    queryKeys = message.reactQueryKey;
                } else {
                    // Single query key array: ["files", fileId]
                    queryKeys = [message.reactQueryKey as string[]];
                }
            } else {
                queryKeys = [];
            }

            // Invalidate all query keys
            for (const key of queryKeys) {
                void queryClient.invalidateQueries({
                    queryKey: Array.isArray(key) ? key : [key],
                });
            }
        }
        switch (message.type) {
            case "success":
                customToast.success(message.message, {
                    id: message.toastId,
                    timeout: message.timeout ?? 3 * 1000,
                });
                break;
            case "error":
                customToast.error(message.message, {
                    id: message.toastId,
                    timeout: message.timeout ?? 10 * 1000,
                });
                break;
            case "loading":
                customToast.loading(message.message, {
                    id: message.toastId,
                    timeout: message.timeout ?? 10 * 60 * 1000,
                });
                break;
            default:
                customToast.success(message.message, {
                    id: message.toastId,
                    timeout: message.timeout ?? 5 * 1000,
                });
                break;
        }
    }, []);

    useEffect(() => {
        if (orgChannel) {
            orgChannel.bind(WEBSOCKET_MESSAGE_TYPES.TOAST_MESSAGE, handleToast);
        }
        return () => {
            orgChannel?.unbind(WEBSOCKET_MESSAGE_TYPES.TOAST_MESSAGE);
        };
    }, [orgChannel, handleToast]);

    useEffect(() => {
        if (userChannel) {
            userChannel.bind(WEBSOCKET_MESSAGE_TYPES.TOAST_MESSAGE, handleToast);
        }
        return () => {
            userChannel?.unbind(WEBSOCKET_MESSAGE_TYPES.TOAST_MESSAGE);
        };
    }, [userChannel, handleToast]);

    return null;
};

export default PushNotification;
